import React, { useState } from "react";
import { Users, Shield, Heart, Upload, Briefcase, Clock, Mail, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Careers = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    dateOfBirth: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    position: "",
    availability: "",
    preferredSchedule: "",
    startDate: "",
    previousEmployer1: "",
    previousPosition1: "",
    previousDuration1: "",
    reasonForLeaving1: "",
    previousEmployer2: "",
    previousPosition2: "",
    previousDuration2: "",
    reasonForLeaving2: "",
    education: "",
    certifications: "",
    experience: "",
    whyJoin: "",
    transportationExperience: "",
    customerServiceExperience: "",
    medicalExperience: "",
    hasLicense: false,
    licenseNumber: "",
    licenseState: "",
    licenseExpiration: "",
    backgroundCheck: false,
    drugScreening: false,
    physicalRequirements: false,
    liftingRequirements: false,
    references: [],
    additionalSkills: "",
    languages: "",
    computerSkills: "",
    resume: null
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'application/pdf' || file.type.includes('word'))) {
      setFormData(prev => ({ ...prev, resume: file }));
      toast.success(`Resume uploaded: ${file.name}`);
    } else {
      toast.error("Please upload a PDF or Word document.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'resume' && formData[key]) {
          formDataToSend.append('resume', formData[key]);
        } else if (key !== 'resume') {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await axios.post(`${API}/career-application`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Reset form
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          address: "",
          city: "",
          state: "",
          zipCode: "",
          dateOfBirth: "",
          emergencyContactName: "",
          emergencyContactPhone: "",
          position: "",
          availability: "",
          preferredSchedule: "",
          startDate: "",
          previousEmployer1: "",
          previousPosition1: "",
          previousDuration1: "",
          reasonForLeaving1: "",
          previousEmployer2: "",
          previousPosition2: "",
          previousDuration2: "",
          reasonForLeaving2: "",
          education: "",
          certifications: "",
          experience: "",
          whyJoin: "",
          transportationExperience: "",
          customerServiceExperience: "",
          medicalExperience: "",
          hasLicense: false,
          licenseNumber: "",
          licenseState: "",
          licenseExpiration: "",
          backgroundCheck: false,
          drugScreening: false,
          physicalRequirements: false,
          liftingRequirements: false,
          references: [],
          additionalSkills: "",
          languages: "",
          computerSkills: "",
          resume: null
        });
        // Reset file input
        const fileInput = document.getElementById('resume');
        if (fileInput) {
          fileInput.value = '';
        }
      }
    } catch (error) {
      console.error("Career application submission error:", error);
      const errorMessage = error.response?.data?.detail || 
                          "There was an error submitting your application. Please try again or contact us directly at (269) 248-3514.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: <Shield className="h-6 w-6 text-blue-900" />,
      title: "Veteran-Friendly Environment",
      description: "Join a team that understands and values military service and experience."
    },
    {
      icon: <Users className="h-6 w-6 text-blue-900" />,
      title: "Family-Like Culture",
      description: "Work in a supportive, family-oriented environment where every team member matters."
    },
    {
      icon: <Heart className="h-6 w-6 text-blue-900" />,
      title: "Meaningful Work",
      description: "Make a difference in people's lives by providing essential transportation services."
    },
    {
      icon: <Clock className="h-6 w-6 text-blue-900" />,
      title: "Flexible Scheduling",
      description: "Opportunities for full-time, part-time, and flexible scheduling options."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Join Our Team</h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Become part of a veteran-owned, family-operated transportation company that makes 
              a real difference in people's lives every day. We're looking for compassionate, 
              reliable team members to join our mission.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center p-6 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="space-y-3">
                  <div className="mx-auto w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Current Openings */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Current Opportunities</h2>
            <p className="text-lg text-gray-600">
              We're actively seeking dedicated professionals to join our transportation team.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Briefcase className="h-5 w-5 text-blue-900" />
                  <span>Transportation Driver</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Provide safe, reliable transportation services for medical appointments and other essential trips.
                </p>
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900">Requirements:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Valid driver's license with clean driving record</li>
                    <li>• Compassionate and patient demeanor</li>
                    <li>• Ability to assist passengers with mobility needs</li>
                    <li>• Professional appearance and communication skills</li>
                    <li>• Background check and drug screening required</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-blue-900" />
                  <span>Office Coordinator</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Handle scheduling, customer service, and administrative tasks to support our transportation operations.
                </p>
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900">Requirements:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Strong organizational and communication skills</li>
                    <li>• Experience with scheduling and customer service</li>
                    <li>• Computer proficiency and phone etiquette</li>
                    <li>• Ability to work in a fast-paced environment</li>
                    <li>• Attention to detail and problem-solving skills</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">Employment Application</CardTitle>
              <p className="text-gray-600">
                Please fill out the form below to apply for a position with Quality Transport Services.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full Legal Name *</Label>
                      <Input
                        id="fullName"
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => handleInputChange("fullName", e.target.value)}
                        placeholder="First Middle Last"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                      <Input
                        id="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder="your.email@example.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        placeholder="(269) 123-4567"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleInputChange("address", e.target.value)}
                      placeholder="Street address"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleInputChange("city", e.target.value)}
                        placeholder="City"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        type="text"
                        value={formData.state}
                        onChange={(e) => handleInputChange("state", e.target.value)}
                        placeholder="MI"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zipCode">ZIP Code *</Label>
                      <Input
                        id="zipCode"
                        type="text"
                        value={formData.zipCode}
                        onChange={(e) => handleInputChange("zipCode", e.target.value)}
                        placeholder="49001"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="emergencyContactName">Emergency Contact Name *</Label>
                      <Input
                        id="emergencyContactName"
                        type="text"
                        value={formData.emergencyContactName}
                        onChange={(e) => handleInputChange("emergencyContactName", e.target.value)}
                        placeholder="Emergency contact full name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergencyContactPhone">Emergency Contact Phone *</Label>
                      <Input
                        id="emergencyContactPhone"
                        type="tel"
                        value={formData.emergencyContactPhone}
                        onChange={(e) => handleInputChange("emergencyContactPhone", e.target.value)}
                        placeholder="(269) 123-4567"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Position Information Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Position Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="position">Desired Position *</Label>
                      <Select value={formData.position} onValueChange={(value) => handleInputChange("position", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a position" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="driver">Transportation Driver</SelectItem>
                          <SelectItem value="office">Office Coordinator</SelectItem>
                          <SelectItem value="both">Open to Both Positions</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="startDate">Available Start Date *</Label>
                      <Input
                        id="startDate"
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => handleInputChange("startDate", e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="availability">Days Available *</Label>
                      <Textarea
                        id="availability"
                        value={formData.availability}
                        onChange={(e) => handleInputChange("availability", e.target.value)}
                        placeholder="Mon-Fri, weekends, etc."
                        rows={3}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preferredSchedule">Preferred Schedule *</Label>
                      <Select value={formData.preferredSchedule} onValueChange={(value) => handleInputChange("preferredSchedule", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select schedule preference" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full-time">Full-time (40+ hours/week)</SelectItem>
                          <SelectItem value="part-time">Part-time (20-39 hours/week)</SelectItem>
                          <SelectItem value="casual">Casual/On-call</SelectItem>
                          <SelectItem value="flexible">Flexible</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Employment History Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Employment History</h3>
                  <div className="space-y-6">
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3">Most Recent Employer</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="previousEmployer1">Company Name</Label>
                          <Input
                            id="previousEmployer1"
                            type="text"
                            value={formData.previousEmployer1}
                            onChange={(e) => handleInputChange("previousEmployer1", e.target.value)}
                            placeholder="Company name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="previousPosition1">Position Held</Label>
                          <Input
                            id="previousPosition1"
                            type="text"
                            value={formData.previousPosition1}
                            onChange={(e) => handleInputChange("previousPosition1", e.target.value)}
                            placeholder="Job title"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="previousDuration1">Employment Duration</Label>
                          <Input
                            id="previousDuration1"
                            type="text"
                            value={formData.previousDuration1}
                            onChange={(e) => handleInputChange("previousDuration1", e.target.value)}
                            placeholder="e.g., Jan 2020 - Dec 2023"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="reasonForLeaving1">Reason for Leaving</Label>
                          <Input
                            id="reasonForLeaving1"
                            type="text"
                            value={formData.reasonForLeaving1}
                            onChange={(e) => handleInputChange("reasonForLeaving1", e.target.value)}
                            placeholder="Reason for leaving"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3">Previous Employer</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="previousEmployer2">Company Name</Label>
                          <Input
                            id="previousEmployer2"
                            type="text"
                            value={formData.previousEmployer2}
                            onChange={(e) => handleInputChange("previousEmployer2", e.target.value)}
                            placeholder="Company name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="previousPosition2">Position Held</Label>
                          <Input
                            id="previousPosition2"
                            type="text"
                            value={formData.previousPosition2}
                            onChange={(e) => handleInputChange("previousPosition2", e.target.value)}
                            placeholder="Job title"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="previousDuration2">Employment Duration</Label>
                          <Input
                            id="previousDuration2"
                            type="text"
                            value={formData.previousDuration2}
                            onChange={(e) => handleInputChange("previousDuration2", e.target.value)}
                            placeholder="e.g., Jan 2018 - Dec 2019"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="reasonForLeaving2">Reason for Leaving</Label>
                          <Input
                            id="reasonForLeaving2"
                            type="text"
                            value={formData.reasonForLeaving2}
                            onChange={(e) => handleInputChange("reasonForLeaving2", e.target.value)}
                            placeholder="Reason for leaving"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Education & Qualifications Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Education & Qualifications</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="education">Highest Level of Education *</Label>
                      <Select value={formData.education} onValueChange={(value) => handleInputChange("education", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select education level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high-school">High School Diploma/GED</SelectItem>
                          <SelectItem value="some-college">Some College</SelectItem>
                          <SelectItem value="associates">Associate's Degree</SelectItem>
                          <SelectItem value="bachelors">Bachelor's Degree</SelectItem>
                          <SelectItem value="masters">Master's Degree</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="certifications">Certifications & Licenses</Label>
                      <Textarea
                        id="certifications"
                        value={formData.certifications}
                        onChange={(e) => handleInputChange("certifications", e.target.value)}
                        placeholder="CPR, First Aid, CDL, Medical certifications, etc."
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="languages">Languages Spoken</Label>
                      <Input
                        id="languages"
                        type="text"
                        value={formData.languages}
                        onChange={(e) => handleInputChange("languages", e.target.value)}
                        placeholder="English, Spanish, etc."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="computerSkills">Computer Skills</Label>
                      <Textarea
                        id="computerSkills"
                        value={formData.computerSkills}
                        onChange={(e) => handleInputChange("computerSkills", e.target.value)}
                        placeholder="Microsoft Office, scheduling software, GPS systems, etc."
                        rows={3}
                      />
                    </div>
                  </div>
                </div>

                {/* Experience Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Relevant Experience</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="transportationExperience">Transportation/Driving Experience</Label>
                      <Textarea
                        id="transportationExperience"
                        value={formData.transportationExperience}
                        onChange={(e) => handleInputChange("transportationExperience", e.target.value)}
                        placeholder="Describe any professional driving, transportation, or delivery experience"
                        rows={4}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="customerServiceExperience">Customer Service Experience</Label>
                      <Textarea
                        id="customerServiceExperience"
                        value={formData.customerServiceExperience}
                        onChange={(e) => handleInputChange("customerServiceExperience", e.target.value)}
                        placeholder="Describe your customer service experience and skills"
                        rows={4}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="medicalExperience">Healthcare/Medical Experience</Label>
                      <Textarea
                        id="medicalExperience"
                        value={formData.medicalExperience}
                        onChange={(e) => handleInputChange("medicalExperience", e.target.value)}
                        placeholder="Any experience in healthcare, working with elderly, disabled, or medical transport"
                        rows={4}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="additionalSkills">Additional Skills & Experience</Label>
                      <Textarea
                        id="additionalSkills"
                        value={formData.additionalSkills}
                        onChange={(e) => handleInputChange("additionalSkills", e.target.value)}
                        placeholder="Any other relevant skills, experiences, or qualifications"
                        rows={4}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="whyJoin">Why do you want to work with Quality Transport Services? *</Label>
                      <Textarea
                        id="whyJoin"
                        value={formData.whyJoin}
                        onChange={(e) => handleInputChange("whyJoin", e.target.value)}
                        placeholder="Tell us what attracts you to working with our veteran-owned, family-operated company"
                        rows={4}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Driver Requirements Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Driver Requirements (If Applicable)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="licenseNumber">Driver's License Number</Label>
                      <Input
                        id="licenseNumber"
                        type="text"
                        value={formData.licenseNumber}
                        onChange={(e) => handleInputChange("licenseNumber", e.target.value)}
                        placeholder="License number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="licenseState">License State</Label>
                      <Input
                        id="licenseState"
                        type="text"
                        value={formData.licenseState}
                        onChange={(e) => handleInputChange("licenseState", e.target.value)}
                        placeholder="MI"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="licenseExpiration">License Expiration Date</Label>
                      <Input
                        id="licenseExpiration"
                        type="date"
                        value={formData.licenseExpiration}
                        onChange={(e) => handleInputChange("licenseExpiration", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 text-sm">
                      <strong>Note:</strong> All transportation is provided using company vehicles with full commercial insurance coverage. 
                      Personal auto insurance is not required for this position.
                    </p>
                  </div>
                </div>

                {/* Requirements & Agreements Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Requirements & Agreements</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="hasLicense"
                        checked={formData.hasLicense}
                        onCheckedChange={(checked) => handleInputChange("hasLicense", checked)}
                      />
                      <Label htmlFor="hasLicense" className="text-sm leading-relaxed">
                        I have a valid driver's license with a clean driving record and am eligible to drive for commercial purposes
                      </Label>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="backgroundCheck"
                        checked={formData.backgroundCheck}
                        onCheckedChange={(checked) => handleInputChange("backgroundCheck", checked)}
                      />
                      <Label htmlFor="backgroundCheck" className="text-sm leading-relaxed">
                        I consent to background check and criminal history verification
                      </Label>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="drugScreening"
                        checked={formData.drugScreening}
                        onCheckedChange={(checked) => handleInputChange("drugScreening", checked)}
                      />
                      <Label htmlFor="drugScreening" className="text-sm leading-relaxed">
                        I consent to pre-employment and random drug/alcohol screening
                      </Label>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="physicalRequirements"
                        checked={formData.physicalRequirements}
                        onCheckedChange={(checked) => handleInputChange("physicalRequirements", checked)}
                      />
                      <Label htmlFor="physicalRequirements" className="text-sm leading-relaxed">
                        I can meet the physical requirements including assisting passengers, operating wheelchair equipment, and standing for extended periods
                      </Label>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="liftingRequirements"
                        checked={formData.liftingRequirements}
                        onCheckedChange={(checked) => handleInputChange("liftingRequirements", checked)}
                      />
                      <Label htmlFor="liftingRequirements" className="text-sm leading-relaxed">
                        I can lift up to 50 pounds and assist patients with mobility needs
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Resume Upload */}
                <div className="space-y-2">
                  <Label htmlFor="resume" className="flex items-center space-x-2">
                    <Upload className="h-4 w-4" />
                    <span>Resume Upload (Optional)</span>
                  </Label>
                  <Input
                    id="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500">
                    Accepted formats: PDF, DOC, DOCX (Max 5MB)
                  </p>
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-blue-900 hover:bg-blue-800"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting Application..." : "Submit Complete Application"}
                  </Button>
                  <p className="text-sm text-gray-500 mt-3 text-center">
                    By submitting this application, you certify that the information provided is true and complete. 
                    You authorize Quality Transport Services to verify this information and contact your references. 
                    We are an equal opportunity employer.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-2">Questions about our open positions?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6">
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-blue-900" />
                <span className="font-semibold text-blue-900">(269) 248-3514</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-blue-900" />
                <span className="text-blue-900">qualitytransportservices42@gmail.com</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;